import { z } from 'zod';
import { AttributeEnum, EntityType, SkillEnum } from "../entity/types.js";
import { PendingLootSchema } from "./loot.schema.js";
import { QuestDTOSchema } from "./quest.schema.js";
import { NpcPublicSchema } from "./character.schema.js";
import { EncounterNodeType, EncounterCheckEvent, EncounterType, } from "../encounter/types.js";
import { Requirement } from "../core/types.js";
import { FeatureType, TerrainType } from "../hex/types.js";
import { CombatStateSchema } from "./combat.schema.js";
// --- Encounter record (DB / DTO) ---
export const EncounterSchema = z.object({
    id: z.string(),
    gameId: z.string(),
    encounterDefId: z.string(),
    x: z.number().int(),
    y: z.number().int(),
    currentNodeId: z.string().nullable(),
    encounterType: z.nativeEnum(EncounterType),
    npcId: z.string().optional(),
    npcName: z.string().optional(),
});
// --- EncounterDef schemas (for API responses) ---
const RangeSchema = z.object({
    min: z.number(),
    max: z.number(),
});
const EncounterEntitiesDefSchema = z.object({
    type: z.nativeEnum(EntityType),
    id: z.string(),
    groupId: z.string(),
    number: RangeSchema,
});
const SkillCheckDefSchema = z.object({
    attribute: z.nativeEnum(AttributeEnum),
    skill: z.nativeEnum(SkillEnum),
    dc: z.number().int(),
    failNode: z.string(),
});
export const SkillCheckResultSchema = z.object({
    attribute: z.nativeEnum(AttributeEnum),
    skill: z.nativeEnum(SkillEnum),
    dc: z.number().int(),
    roll: z.number().int(),
    modifier: z.number().int(),
    total: z.number().int(),
    success: z.boolean(),
});
const ExitFilterSchema = z.object({
    type: z.string(),
    params: z.record(z.string(), z.string()),
});
const EncounterNodeExitSchema = z.object({
    node: z.string(),
    text: z.string(),
    skillCheck: SkillCheckDefSchema.optional(),
    filters: z.array(ExitFilterSchema).optional(),
});
const EncounterEffectSchema = z.object({
    type: z.string(),
    params: z.preprocess((val) => {
        if (val && typeof val === 'object') {
            return Object.fromEntries(Object.entries(val).filter(([, v]) => v !== null));
        }
        return val;
    }, z.record(z.string(), z.string())),
});
export const AppliedEffectSchema = z.object({
    type: z.string(),
    params: z.record(z.string(), z.string()),
    message: z.string(),
    positive: z.boolean(),
});
const EncounterNodeCombatSchema = z.object({
    id: z.string(),
    type: z.literal(EncounterNodeType.COMBAT),
    entities: z.array(EncounterEntitiesDefSchema),
    winExit: z.string().optional(),
    loseExit: z.string().optional(),
    winEffects: z.array(EncounterEffectSchema).optional(),
    loseEffects: z.array(EncounterEffectSchema).optional(),
    exits: z.array(EncounterNodeExitSchema).optional(),
    logTemplate: z.string().optional(),
});
const EncounterNodeTextSchema = z.object({
    id: z.string(),
    type: z.literal(EncounterNodeType.TEXT),
    text: z.string(),
    exits: z.array(EncounterNodeExitSchema).optional(),
    effects: z.array(EncounterEffectSchema).optional(),
    logTemplate: z.string().optional(),
    outcomeSummary: z.string().optional(),
});
const EncounterNodeSchema = z.discriminatedUnion('type', [
    EncounterNodeCombatSchema,
    EncounterNodeTextSchema,
]);
const EncounterFiltersSchema = z.object({
    level: RangeSchema,
    terrain: z.array(z.nativeEnum(TerrainType)),
    features: z.array(z.nativeEnum(FeatureType)),
    river: z.nativeEnum(Requirement),
    road: z.nativeEnum(Requirement),
    events: z.array(z.nativeEnum(EncounterCheckEvent)),
});
export const EncounterDefSchema = z.object({
    id: z.string(),
    filters: EncounterFiltersSchema,
    startNode: z.string(),
    nodes: z.array(EncounterNodeSchema),
    noRepeat: z.boolean().optional(),
    weight: z.number().optional(),
});
// --- Request/Response schemas for encounter endpoints ---
// Cheat override that forces a skill-check exit to succeed or fail without rolling.
// Available to all players during early development.
export const SkillCheckOutcomeSchema = z.enum(['success', 'failure']);
export const UpdateNodeRequestSchema = z.object({
    encounterId: z.string(),
    nodeId: z.string().nullable(),
    cheatForceSkillCheck: SkillCheckOutcomeSchema.optional(),
});
const EncounterNodeExitDTOSchema = z.object({
    node: z.string(),
    text: z.string(),
    skillCheck: SkillCheckDefSchema.optional(),
});
export const EncounterTextNodeDTOSchema = z.object({
    id: z.string(),
    type: z.literal(EncounterNodeType.TEXT),
    text: z.string(),
    exits: z.array(EncounterNodeExitDTOSchema),
});
export const EncounterCombatNodeDTOSchema = z.object({
    id: z.string(),
    type: z.literal(EncounterNodeType.COMBAT),
});
export const EncounterCurrentNodeDTOSchema = z.discriminatedUnion('type', [
    EncounterTextNodeDTOSchema,
    EncounterCombatNodeDTOSchema,
]);
export const UpdateNodeResponseSchema = z.object({
    encounter: EncounterSchema,
    node: EncounterCurrentNodeDTOSchema,
    combat: CombatStateSchema.optional(),
    skillCheckResult: SkillCheckResultSchema.optional(),
    appliedEffects: z.array(AppliedEffectSchema).optional(),
    pendingLoot: PendingLootSchema.optional(),
    discoveredPlaceIds: z.array(z.string()).optional(),
    discoveredItemIds: z.array(z.string()).optional(),
    discoveredNpcIds: z.array(z.string()).optional(),
    quest: QuestDTOSchema.optional(),
    hiredNpc: NpcPublicSchema.optional(),
});
export const CompleteEncounterRequestSchema = z.object({
    encounterId: z.string(),
});
export const CompleteEncounterResponseSchema = z.object({
    appliedEffects: z.array(AppliedEffectSchema).optional(),
    pendingLoot: PendingLootSchema.optional(),
    // When a non-companion quest was just turned in at its giver, the id of that NPC.
    // The client opens a fresh follow-on conversation with them once any reward loot
    // has been dismissed (see /game/encounter/npc-continuation).
    followUpNpcId: z.string().optional(),
});
export const NpcContinuationRequestSchema = z.object({
    gameId: z.string(),
    npcId: z.string(),
});
// Empty (no `encounter`) when the follow-up could not be opened — the client simply
// does nothing in that case.
export const NpcContinuationResponseSchema = z.object({
    encounter: EncounterSchema.optional(),
    npcName: z.string().optional(),
});
// --- NPC encounter location (for map markers) ---
export const NpcEncounterLocationSchema = z.object({
    encounterId: z.string(),
    npcId: z.string(),
    npcName: z.string(),
    archetypeId: z.string(),
    x: z.number().int(),
    y: z.number().int(),
    showOnMap: z.boolean(),
});
export const NpcEncounterLocationsResponseSchema = z.object({
    locations: z.array(NpcEncounterLocationSchema),
});
